/* eslint-disable no-dupe-keys */

import PropTypes from 'prop-types';
import { Box, Button, Container, Flex, Text } from 'theme-ui';

import { Link } from 'gatsby';
import ImgLoader from '../ImgLoader';

const ImageHeroWithoutGradient = ({
  title,
  titleBottom,
  image,
  subtitle,
  body,
  alignment,
  cta,
  primary,
  titlePosition,
  invertColors,
  forwardSx,
}) => {
  const titleBottomSplit = titleBottom && titleBottom.split(' ');
  return (
    <Box
      as="section"
      sx={{
        '--image-height': primary ? ['52.4rem', null, '91.2rem'] : ['62rem', '53rem', '73rem'],
        position: 'relative',
        display: 'flex',
        flexFlow: 'column nowrap',
        alignItems: 'stretch',
        '::before': {
          content: '""',
          position: 'absolute',
          width: '100%',
          height: ['30%', '60%'],
          background: [null, primary ? 'white' : 'transparent'],
          top: 0,
          zIndex: -1,
        },
        overflow: 'hidden',
        ...forwardSx,
      }}
    >
      <Container
        sx={{
          order: titlePosition === 'Top' ? 0 : 1,
          color: invertColors ? 'white' : 'black',
          h1: {
            maxWidth: ['430px', '580px', '850px'],
            fontSize: primary
              ? ['clamp(7rem, 20vw, 7.8rem)', 'min(11.2rem, 19vw)', 6]
              : ['clamp(7rem, 20vw, 7.8rem)', null, '10.9rem'],
          },
          p: {
            maxWidth: ['430px', '580px', '850px'],
          },
          py: 0,
        }}
      >
        {titlePosition === 'Top' && subtitle && (
          <Text as="p" variant="text.h3" sx={{ lineHeight: '100%' }}>
            {subtitle}
          </Text>
        )}

        {titlePosition === 'Top' && title && (
          <Text
            as="h1"
            variant="h1"
            sx={{
              mb: primary ? '20px' : '12px',
              mt: primary ? [null, '14.9rem', '22.2rem'] : ['10rem', '13.6rem', '17.8rem'],
            }}
          >
            {title}
          </Text>
        )}

        {titlePosition === 'Bottom' && titleBottomSplit && (
          <Text
            as="h1"
            variant="h1"
            sx={{
              color: invertColors ? 'white' : 'black',
              display: 'flex',
              flexWrap: ['wrap', null, 'nowrap'],
              mt: '5px',
              wordWrap: 'anywhere',
              wordBreak: 'break-word',
              whiteSpace: [null, null, 'nowrap'],
            }}
          >
            {titleBottomSplit.map((word, i) => (
              <div key={word + i}>{word}</div>
            ))}
          </Text>
        )}
      </Container>

      <Box
        sx={{
          width: '100%',
          minHeight: [null, null, '730px'],
          pb: 'var(--image-height)',
          position: 'relative',
          borderRadius: [
            `${primary ? '0' : '25px'} 0 75px`,
            `${titlePosition === 'Top' ? '25px' : 0} 0 75px`,
            `${titlePosition === 'Top' ? '50px' : 0} 0px 180px`,
          ],
          WebkitMaskImage: '-webkit-radial-gradient(white, black)',
          overflow: 'hidden',
        }}
      >
        <Container
          sx={{
            position: 'absolute',
            top: titlePosition === 'Top' ? ['-8px', '-10px', '-14px'] : 'auto',
            left: '50%',
            bottom: titlePosition === 'Top' ? 'auto' : 0,
            color: invertColors ? 'black' : 'white',
            py: 0,
            transform: 'translateX(-50%)',
            zIndex: 1,
            mb: titlePosition === 'Bottom' && title ? ['-0.9rem', '-1.3rem'] : 0,
            h1: {
              fontSize: primary
                ? ['clamp(7rem, 19vw, 7.8rem)', 'min(11.2rem, 19vw)', 6]
                : ['clamp(7rem, 19vw, 7.8rem)', null, '10.9rem'],
            },
          }}
        >
          {titlePosition === 'Top' && titleBottomSplit && (
            <Text
              as="h1"
              variant="h1"
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                wordWrap: 'anywhere',
              }}
            >
              {titleBottomSplit.map((word, i) => (
                <div key={word + i}>{word}</div>
              ))}
            </Text>
          )}

          {titlePosition === 'Bottom' && title && (
            <Text
              as="h1"
              variant="h1"
              sx={{
                maxWidth: ['430px', '580px', '850px'],
                display: 'inline-block',
              }}
            >
              {subtitle ? (
                <span
                  sx={{
                    display: 'block',
                    fontSize: [3, null, 4],
                    color: invertColors ? 'white' : 'black',
                    pl: 1,
                    mb: [4, null, '5.6rem'],
                    lineHeight: '100%',
                  }}
                >
                  {subtitle}
                </span>
              ) : null}

              {title}
            </Text>
          )}
        </Container>

        {image?.file.contentType.split('/')[0] === 'video' ? (
          <div
            sx={{
              ':after': {
                position: 'absolute',
                content: '""',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                background: 'rgba(0,0,0,0.12)',
              },
            }}
          >
            <video
              muted
              sx={{
                height: '100%',
                minHeight: '300px',
                height: 'var(--image-height)',
                position: 'absolute !important',
                width: '100%',
                objectFit: 'cover',
              }}
              loop
              playsInline
              autoPlay
            >
              <source src={image.file.url} type={image.file.contentType} />
            </video>
          </div>
        ) : (
          <ImgLoader
            image={image}
            sx={{
              height: '100%',
              minHeight: '300px',
              height: 'var(--image-height)',
              position: 'absolute !important',
              width: '100%',
              transform: alignment === 'Right' ? 'scaleX(-1)' : null,
            }}
          />
        )}
      </Box>

      <Container
        sx={{
          order: 2,
          display: body || cta ? 'flex' : 'none',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          alignItems: alignment === 'Left' ? 'flex-start' : 'flex-end',
          position: ['relative', null, 'absolute'],
          px: [4, '10rem', '155px'],
          pt: ['3.7rem', 5],
          pb: [null, null, 8],
          top: 0,
          bottom: 0,
          left: 0,
          right: 0,
          color: ['black', null, 'white'],
        }}
      >
        <Flex sx={{ flexDirection: 'column', width: ['100%', 'auto', null] }}>
          {body && (
            <Text
              variant="large"
              sx={{
                fontSize: [3, 3, 3],
                maxWidth: ['350px', '645px'],
                mb: [5, '3.8rem'],
              }}
            >
              {body.body}
            </Text>
          )}

          {cta && (
            <Box>
              {cta.url ? (
                <a href={cta.url} target="_blank" rel="noopener noreferrer">
                  {cta.text}
                </a>
              ) : (
                <Button
                  as={Link}
                  to={`${cta.page.slug}/`}
                  sx={{
                    variant: ['buttons.primary', null, 'buttons.knockout'],
                    minWidth: ['min(36.6rem, 100%)', 0, null],
                  }}
                >
                  {cta.text}
                </Button>
              )}
            </Box>
          )}
        </Flex>
      </Container>
    </Box>
  );
};

export const ImageHero = ({ gradient = false, ...props }) => {
  return <ImageHeroWithoutGradient {...props} />;
};

ImageHero.propTypes = {
  title: PropTypes.string,
  titleBottom: PropTypes.string,
  subtitle: PropTypes.string,
  image: PropTypes.shape({
    fluid: PropTypes.object.isRequired,
  }).isRequired,
  cta: PropTypes.shape({
    text: PropTypes.isRequired,
    url: PropTypes.string,
    page: PropTypes.shape({
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
    }),
  }),
  alignment: PropTypes.oneOf(['Left', 'Right']),
  titlePosition: PropTypes.oneOf(['Top', 'Bottom']).isRequired,
  primary: PropTypes.bool,
  invertColors: PropTypes.bool,
  forwardSx: PropTypes.shape({}),
};

ImageHero.defaultProps = {
  titlePosition: 'Top',
  primary: false,
  alignment: 'Left',
  invertColors: false,
  forwardSx: {},
};

export default ImageHero;
